import { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2, Plus, Save } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { importTrackingService } from '../services/import-tracking-service';
import { freightServicesService } from '../services/freight-services-service';
import {
  freightProviderService,
  type FreightProvider,
} from '@/features/master-data/services/freight-provider-service';
import type { FreightService } from '../types/import-tracking.types';
import {
  commercialService,
  type CommercialSummary,
  type CommercialLegRow,
  type CommercialServiceRow,
} from '../services/commercial-service';

// ── Helpers ───────────────────────────────────────────────────────

function parseNumber(val: string): number | null {
  const trimmed = val.trim();
  if (trimmed === '' || trimmed === '-') return null;
  const n = parseFloat(trimmed);
  return isNaN(n) ? null : n;
}

function fmtCurrency(value: number | null, currency: string): string {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

function fmtPct(value: number | null): string {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(2)}%`;
}

function MarginCell({
  margin,
  pct,
  currency,
}: {
  margin: number | null;
  pct: number | null;
  currency: string;
}) {
  if (margin === null) return <span className="text-muted-foreground">-</span>;
  const color = margin >= 0 ? 'text-ok' : 'text-bloqueo';
  return (
    <span className={color}>
      {fmtCurrency(margin, currency)}
      {pct !== null && <span className="ml-1 text-xs">({fmtPct(pct)})</span>}
    </span>
  );
}

// ── Transport mode labels ─────────────────────────────────────────

const TRANSPORT_EMOJI: Record<string, string> = {
  SEA: '🚢',
  AIR: '✈️',
  LAND: '🚛',
  RAIL: '🚂',
  MULTIMODAL: '🔀',
};

const TRANSPORT_LABEL: Record<string, string> = {
  SEA: 'Marítimo',
  AIR: 'Aéreo',
  LAND: 'Terrestre',
  RAIL: 'Ferroviario',
  MULTIMODAL: 'Multimodal',
};

const LEG_TYPE_LABEL: Record<string, string> = {
  INLAND_ORIGIN: 'Origen',
  INTERNATIONAL: 'Internacional',
  INLAND_DESTINATION: 'Destino',
  HANDLING: 'Manejo',
};

// ── Leg editing state ─────────────────────────────────────────────

interface LegEditState {
  providerCostAmount: string;
  customerSalePrice: string;
  saving: boolean;
}

// ── Service dialog ────────────────────────────────────────────────

interface ServiceFormState {
  legId: string;
  serviceId: string;
  providerId: string;
  name: string;
  providerCostAmount: string;
  customerSalePrice: string;
  notes: string;
}

const emptyServiceForm = (defaultLegId = ''): ServiceFormState => ({
  legId: defaultLegId,
  serviceId: '',
  providerId: '',
  name: '',
  providerCostAmount: '',
  customerSalePrice: '',
  notes: '',
});

interface ServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  legs: CommercialLegRow[];
  currency: string;
  editingService: CommercialServiceRow | null;
  onSave: (form: ServiceFormState) => Promise<void>;
}

function ServiceDialog({
  open,
  onOpenChange,
  legs,
  currency,
  editingService,
  onSave,
}: ServiceDialogProps) {
  const [form, setForm] = useState<ServiceFormState>(emptyServiceForm(legs[0]?.id ?? ''));
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState<FreightService[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [providers, setProviders] = useState<FreightProvider[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  // Cargar catálogo de servicios al abrir (solo cuando NO es edición)
  useEffect(() => {
    if (!open || editingService) return;
    setLoadingCatalog(true);
    freightServicesService
      .list()
      .then((res: any) => {
        const items = Array.isArray(res) ? res : (res?.data ?? []);
        setCatalog(items.filter((s: FreightService) => s.isActive));
      })
      .catch(() => setCatalog([]))
      .finally(() => setLoadingCatalog(false));
  }, [open, editingService]);

  // Cargar proveedores al abrir (siempre, para create y edit).
  // Filtramos a sólo los activos QUE TIENEN modos de transporte declarados —
  // así excluimos proveedores de mercadería (suppliers) que viven en la
  // misma tabla freight_providers pero no son carriers.
  useEffect(() => {
    if (!open) return;
    setLoadingProviders(true);
    freightProviderService
      .getAll()
      .then((items) =>
        setProviders(
          (items ?? []).filter(
            (p) => p.isActive && Array.isArray(p.transportModes) && p.transportModes.length > 0
          )
        )
      )
      .catch(() => setProviders([]))
      .finally(() => setLoadingProviders(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editingService) {
      setForm({
        legId: editingService.legId,
        serviceId: '',
        providerId: editingService.provider?.id ?? '',
        name: editingService.name,
        providerCostAmount:
          editingService.providerCostAmount !== null
            ? String(editingService.providerCostAmount)
            : '',
        customerSalePrice:
          editingService.customerSalePrice !== null ? String(editingService.customerSalePrice) : '',
        notes: '',
      });
    } else {
      setForm(emptyServiceForm(legs[0]?.id ?? ''));
    }
  }, [open, editingService, legs]);

  const handleCatalogPick = (svcId: string) => {
    const svc = catalog.find((s) => s.id === svcId);
    if (!svc) {
      setForm((f) => ({ ...f, serviceId: '', name: '' }));
      return;
    }
    const refPrice = svc.referencePrice != null ? String(svc.referencePrice) : '';
    setForm((f) => ({
      ...f,
      serviceId: svc.id,
      name: svc.name,
      // Pre-llenar precio venta con el referencePrice del catálogo (si no hay valor previo)
      customerSalePrice: f.customerSalePrice || refPrice,
    }));
  };

  const canSave = editingService
    ? !!form.name.trim() && !!form.providerId
    : !!form.serviceId && !!form.legId && !!form.providerId;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(form);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editingService ? 'Editar servicio' : 'Agregar servicio'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Tramo */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Tramo</label>
            <SearchableSelect
              value={form.legId}
              onValueChange={(val) => setForm((f) => ({ ...f, legId: val }))}
              disabled={!!editingService}
              options={legs.map((leg) => ({
                value: leg.id,
                label: `${leg.legOrder}. ${leg.origin} → ${leg.destination}`,
              }))}
              placeholder="Seleccionar tramo..."
            />
          </div>

          {/* Servicio (catálogo Datos Maestros) */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Servicio <span className="text-destructive">*</span>
            </label>
            {editingService ? (
              <input
                type="text"
                readOnly
                className="w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
                value={form.name}
              />
            ) : (
              <SearchableSelect
                value={form.serviceId}
                onValueChange={(val) => handleCatalogPick(val)}
                disabled={loadingCatalog || catalog.length === 0}
                options={catalog.map((s) => ({
                  value: s.id,
                  label: `${s.code ? `${s.code} · ` : ''}${s.name}${s.category ? ` (${s.category})` : ''}`,
                }))}
                placeholder={
                  loadingCatalog
                    ? 'Cargando catálogo...'
                    : catalog.length === 0
                      ? 'Sin servicios en el catálogo'
                      : '— Seleccionar servicio —'
                }
              />
            )}
            {!editingService && catalog.length === 0 && !loadingCatalog && (
              <p className="text-xs text-muted-foreground">
                Cargá servicios en Datos Maestros → Servicios de flete antes de agregar acá.
              </p>
            )}
          </div>

          {/* Proveedor (catálogo Datos Maestros) */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Proveedor <span className="text-destructive">*</span>
            </label>
            <SearchableSelect
              value={form.providerId}
              onValueChange={(val) => setForm((f) => ({ ...f, providerId: val }))}
              disabled={loadingProviders || providers.length === 0}
              options={providers.map((p) => ({ value: p.id, label: p.name }))}
              placeholder={
                loadingProviders
                  ? 'Cargando proveedores...'
                  : providers.length === 0
                    ? 'Sin proveedores activos'
                    : '— Seleccionar proveedor —'
              }
            />
          </div>

          {/* Costo proveedor */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Costo proveedor ({currency})</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="0.00"
              value={form.providerCostAmount}
              onChange={(e) => setForm((f) => ({ ...f, providerCostAmount: e.target.value }))}
            />
          </div>

          {/* Precio venta */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Precio venta ({currency})</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="0.00"
              value={form.customerSalePrice}
              onChange={(e) => setForm((f) => ({ ...f, customerSalePrice: e.target.value }))}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={saving}>
              Cancelar
            </Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={saving || !canSave}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Custom hook ───────────────────────────────────────────────────

export interface CommercialSummaryHook {
  summary: CommercialSummary | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCommercialSummary(orderId: string): CommercialSummaryHook {
  const [summary, setSummary] = useState<CommercialSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    try {
      setError(null);
      const data = await commercialService.getSummary(orderId);
      setSummary(data);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Error al cargar el resumen comercial.');
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { summary, isLoading, error, refetch };
}

// ── CommercialLegsTable ───────────────────────────────────────────

interface CommercialLegsTableProps {
  summary: CommercialSummary;
  onUpdated: () => Promise<void>;
}

export function CommercialLegsTable({ summary, onUpdated }: CommercialLegsTableProps) {
  const { toast } = useToast();
  const { legs } = summary;
  const currency = summary.header.currency || 'USD';

  const [legEdits, setLegEdits] = useState<Record<string, LegEditState>>(() => {
    const initial: Record<string, LegEditState> = {};
    for (const leg of legs) {
      initial[leg.id] = {
        providerCostAmount: leg.providerCostAmount !== null ? String(leg.providerCostAmount) : '',
        customerSalePrice: leg.customerSalePrice !== null ? String(leg.customerSalePrice) : '',
        saving: false,
      };
    }
    return initial;
  });

  // Re-sync edits when summary legs change (after refetch)
  useEffect(() => {
    setLegEdits((prev) => {
      const next: Record<string, LegEditState> = {};
      for (const leg of legs) {
        next[leg.id] = prev[leg.id] ?? {
          providerCostAmount: leg.providerCostAmount !== null ? String(leg.providerCostAmount) : '',
          customerSalePrice: leg.customerSalePrice !== null ? String(leg.customerSalePrice) : '',
          saving: false,
        };
      }
      return next;
    });
  }, [legs]);

  const saveLeg = useCallback(
    async (leg: CommercialLegRow) => {
      const edit = legEdits[leg.id];
      if (!edit) return;
      setLegEdits((prev) => ({ ...prev, [leg.id]: { ...prev[leg.id], saving: true } }));
      try {
        const payload = {
          providerCostAmount: parseNumber(edit.providerCostAmount),
          customerSalePrice: parseNumber(edit.customerSalePrice),
        };
        console.log('[commercial] PATCH leg', leg.id, payload);
        await importTrackingService.updateLeg(leg.id, payload as any);
        toast({ title: 'Tramo actualizado' });
        await onUpdated();
        // Liberar el input: si no, queda disabled=true para siempre y solo se
        // libera con un refresh manual.
        setLegEdits((prev) => ({ ...prev, [leg.id]: { ...prev[leg.id], saving: false } }));
      } catch (err: any) {
        const status = err?.response?.status;
        const data = err?.response?.data;
        const body =
          typeof data === 'string'
            ? data.slice(0, 200)
            : (data?.error ?? data?.message ?? JSON.stringify(data ?? {}).slice(0, 200));
        console.error('[commercial] error guardando leg', { status, data });
        toast({
          title: `Error al guardar tramo${status ? ` (${status})` : ''}`,
          description: body || 'Error desconocido',
          variant: 'destructive',
        });
        setLegEdits((prev) => ({ ...prev, [leg.id]: { ...prev[leg.id], saving: false } }));
      }
    },
    [legEdits, onUpdated, toast]
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Tramos</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {legs.length === 0 ? (
          <p className="px-6 py-4 text-sm text-muted-foreground">Sin tramos registrados.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  <TableHead>Ruta</TableHead>
                  <TableHead>Modo</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Proveedor</TableHead>
                  <TableHead className="min-w-[140px]">Costo proveedor</TableHead>
                  <TableHead className="min-w-[140px]">Precio venta</TableHead>
                  <TableHead>Margen</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {legs.map((leg) => {
                  const edit = legEdits[leg.id] ?? {
                    providerCostAmount: '',
                    customerSalePrice: '',
                    saving: false,
                  };
                  return (
                    <TableRow key={leg.id}>
                      <TableCell className="text-muted-foreground">{leg.legOrder}</TableCell>
                      <TableCell className="font-medium whitespace-nowrap">
                        {leg.origin} → {leg.destination}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span>{TRANSPORT_EMOJI[leg.transportMode] ?? ''}</span>{' '}
                        <span className="ml-1 text-xs">
                          {TRANSPORT_LABEL[leg.transportMode] ?? leg.transportMode}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">
                          {LEG_TYPE_LABEL[leg.legType] ?? leg.legType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {leg.provider?.name ?? '-'}
                      </TableCell>
                      <TableCell>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-32 rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          value={edit.providerCostAmount}
                          onChange={(e) =>
                            setLegEdits((prev) => ({
                              ...prev,
                              [leg.id]: { ...prev[leg.id], providerCostAmount: e.target.value },
                            }))
                          }
                          onBlur={() => saveLeg(leg)}
                          disabled={edit.saving}
                          placeholder="0.00"
                        />
                      </TableCell>
                      <TableCell>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-32 rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          value={edit.customerSalePrice}
                          onChange={(e) =>
                            setLegEdits((prev) => ({
                              ...prev,
                              [leg.id]: { ...prev[leg.id], customerSalePrice: e.target.value },
                            }))
                          }
                          onBlur={() => saveLeg(leg)}
                          disabled={edit.saving}
                          placeholder="0.00"
                        />
                      </TableCell>
                      <TableCell>
                        <MarginCell margin={leg.margin} pct={leg.marginPct} currency={currency} />
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          title="Guardar"
                          disabled={edit.saving}
                          onClick={() => saveLeg(leg)}
                          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── CommercialServicesTable ───────────────────────────────────────

interface CommercialServicesTableProps {
  orderId: string;
  summary: CommercialSummary;
  onUpdated: () => Promise<void>;
}

interface ServiceEditState {
  providerCostAmount: string;
  customerSalePrice: string;
  saving: boolean;
}

export function CommercialServicesTable({
  orderId,
  summary,
  onUpdated,
}: CommercialServicesTableProps) {
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const { legs, services } = summary;
  const currency = summary.header.currency || 'USD';

  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<CommercialServiceRow | null>(null);

  const [svcEdits, setSvcEdits] = useState<Record<string, ServiceEditState>>(() => {
    const initial: Record<string, ServiceEditState> = {};
    for (const svc of services) {
      initial[svc.id] = {
        providerCostAmount: svc.providerCostAmount !== null ? String(svc.providerCostAmount) : '',
        customerSalePrice: svc.customerSalePrice !== null ? String(svc.customerSalePrice) : '',
        saving: false,
      };
    }
    return initial;
  });

  // Re-sync edits cuando el summary cambia (post refetch)
  useEffect(() => {
    setSvcEdits((prev) => {
      const next: Record<string, ServiceEditState> = {};
      for (const svc of services) {
        next[svc.id] = prev[svc.id] ?? {
          providerCostAmount: svc.providerCostAmount !== null ? String(svc.providerCostAmount) : '',
          customerSalePrice: svc.customerSalePrice !== null ? String(svc.customerSalePrice) : '',
          saving: false,
        };
      }
      return next;
    });
  }, [services]);

  const saveSvc = useCallback(
    async (svc: CommercialServiceRow) => {
      const edit = svcEdits[svc.id];
      if (!edit) return;
      setSvcEdits((prev) => ({ ...prev, [svc.id]: { ...prev[svc.id], saving: true } }));
      try {
        const payload = {
          providerCostAmount: parseNumber(edit.providerCostAmount),
          customerSalePrice: parseNumber(edit.customerSalePrice),
        };
        console.log('[commercial] PATCH service', svc.id, payload);
        await importTrackingService.updateLegService(svc.legId, svc.id, payload as any);
        toast({ title: 'Servicio actualizado' });
        await onUpdated();
        setSvcEdits((prev) => ({ ...prev, [svc.id]: { ...prev[svc.id], saving: false } }));
      } catch (err: any) {
        const status = err?.response?.status;
        const data = err?.response?.data;
        const body =
          typeof data === 'string'
            ? data.slice(0, 200)
            : (data?.error ?? data?.message ?? JSON.stringify(data ?? {}).slice(0, 200));
        console.error('[commercial] error guardando servicio', { status, data });
        toast({
          title: `Error al guardar servicio${status ? ` (${status})` : ''}`,
          description: body || 'Error desconocido',
          variant: 'destructive',
        });
        setSvcEdits((prev) => ({ ...prev, [svc.id]: { ...prev[svc.id], saving: false } }));
      }
    },
    [svcEdits, onUpdated, toast]
  );

  const legMap = new Map(legs.map((l) => [l.id, l]));

  const handleAddService = () => {
    setEditingService(null);
    setServiceDialogOpen(true);
  };

  const handleEditService = (svc: CommercialServiceRow) => {
    setEditingService(svc);
    setServiceDialogOpen(true);
  };

  const handleDeleteService = (svc: CommercialServiceRow) => {
    confirm({
      title: 'Eliminar servicio',
      description: `¿Eliminar "${svc.name}"?`,
      variant: 'destructive',
      confirmText: 'Eliminar',
      onConfirm: async () => {
        await importTrackingService.deleteLegService(svc.legId, svc.id);
        toast({ title: 'Servicio eliminado' });
        await onUpdated();
      },
    });
  };

  const handleSaveService = async (form: ServiceFormState) => {
    const providerCostAmount = parseNumber(form.providerCostAmount);
    const customerSalePrice = parseNumber(form.customerSalePrice);

    if (editingService) {
      await importTrackingService.updateLegService(editingService.legId, editingService.id, {
        name: form.name,
        providerId: form.providerId || null,
        providerCostAmount: providerCostAmount,
        customerSalePrice: customerSalePrice,
      } as any);
      toast({ title: 'Servicio actualizado' });
    } else {
      await importTrackingService.createLegService(form.legId, {
        serviceId: form.serviceId,
        providerId: form.providerId || null,
        name: form.name,
        pricingType: 'FLAT',
        unitPrice: customerSalePrice ?? 0,
        currency: currency,
        providerCostAmount: providerCostAmount,
        customerSalePrice: customerSalePrice,
      } as any);
      toast({ title: 'Servicio creado' });
    }
    await onUpdated();
  };

  // Suppress unused orderId warning — kept for API symmetry / future use
  void orderId;

  return (
    <>
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Servicios</CardTitle>
          <Button size="sm" variant="outline" onClick={handleAddService}>
            <Plus className="w-4 h-4 mr-1" />
            Agregar servicio
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {services.length === 0 ? (
            <p className="px-6 py-4 text-sm text-muted-foreground">Sin servicios registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tramo</TableHead>
                    <TableHead>Servicio</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead className="min-w-[140px]">Costo proveedor</TableHead>
                    <TableHead className="min-w-[140px]">Precio venta</TableHead>
                    <TableHead>Margen</TableHead>
                    <TableHead className="w-10"></TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {services.map((svc) => {
                    const leg = legMap.get(svc.legId);
                    const edit = svcEdits[svc.id] ?? {
                      providerCostAmount: '',
                      customerSalePrice: '',
                      saving: false,
                    };
                    return (
                      <TableRow key={svc.id}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {leg ? `${leg.origin} → ${leg.destination}` : '-'}
                        </TableCell>
                        <TableCell className="font-medium">{svc.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {svc.provider?.name ?? '-'}
                        </TableCell>
                        <TableCell>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-32 rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                            placeholder="0.00"
                            value={edit.providerCostAmount}
                            onChange={(e) =>
                              setSvcEdits((prev) => ({
                                ...prev,
                                [svc.id]: {
                                  ...prev[svc.id],
                                  providerCostAmount: e.target.value,
                                },
                              }))
                            }
                            onBlur={() => saveSvc(svc)}
                            disabled={edit.saving}
                          />
                        </TableCell>
                        <TableCell>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-32 rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                            placeholder="0.00"
                            value={edit.customerSalePrice}
                            onChange={(e) =>
                              setSvcEdits((prev) => ({
                                ...prev,
                                [svc.id]: {
                                  ...prev[svc.id],
                                  customerSalePrice: e.target.value,
                                },
                              }))
                            }
                            onBlur={() => saveSvc(svc)}
                            disabled={edit.saving}
                          />
                        </TableCell>
                        <TableCell>
                          <MarginCell margin={svc.margin} pct={svc.marginPct} currency={currency} />
                        </TableCell>
                        <TableCell>
                          <button
                            type="button"
                            title="Guardar"
                            onClick={() => saveSvc(svc)}
                            disabled={edit.saving}
                            className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              title="Editar"
                              onClick={() => handleEditService(svc)}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              title="Eliminar"
                              onClick={() => handleDeleteService(svc)}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ServiceDialog
        open={serviceDialogOpen}
        onOpenChange={setServiceDialogOpen}
        legs={legs}
        currency={currency}
        editingService={editingService}
        onSave={handleSaveService}
      />
      <ConfirmDialog />
    </>
  );
}

// ── CommercialTotalsCard ──────────────────────────────────────────

interface CommercialTotalsCardProps {
  summary: CommercialSummary;
}

export function CommercialTotalsCard({ summary }: CommercialTotalsCardProps) {
  const { totals, header } = summary;
  const currency = header.currency || 'USD';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Totales</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="rounded-lg border bg-muted/30 p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Total Costo
            </p>
            <p className="text-lg font-semibold">{fmtCurrency(totals.totalCost, currency)}</p>
          </div>
          <div className="rounded-lg border bg-muted/30 p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              Total Venta
            </p>
            <p className="text-lg font-semibold">{fmtCurrency(totals.totalSale, currency)}</p>
          </div>
          <div className="rounded-lg border bg-muted/30 p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Margen ($)</p>
            <p
              className={`text-lg font-semibold ${totals.totalMargin >= 0 ? 'text-ok' : 'text-bloqueo'}`}
            >
              {fmtCurrency(totals.totalMargin, currency)}
            </p>
          </div>
          <div className="rounded-lg border bg-muted/30 p-4 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Margen (%)</p>
            <p
              className={`text-lg font-semibold ${totals.totalMarginPct !== null && totals.totalMarginPct >= 0 ? 'text-ok' : 'text-bloqueo'}`}
            >
              {fmtPct(totals.totalMarginPct)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
